import { SelectOption } from 'components/Forms';
import { TherapyTarget } from 'domains/signup/data/types';
import { TherapyConcerns } from 'domains/signupv3/Mini-Screener/miniScreenerMachine';
import i18next from 'i18next';
import { isNull, isNumber, uniq } from 'lodash';

export type DevelopmentalArea =
  | 'early language development'
  | 'pediatric articulation/phonology'
  | 'fluency'
  | 'accent modification'
  | 'professional/public speaking'
  | 'gender affirming voice'
  | 'adult articulation'
  | 'adult fluency'
  | 'pediatric receptive-expressive language disorder';

export const DevelopmentalAreaWhitelist = [
  'early language development',
  'pediatric articulation/phonology',
  'pediatric receptive-expressive language disorder',
  'fluency',
  'pediatric social-pragmatics',
  'adult articulation',
];

const CONCERN_TO_AREA_MAP_PEDIATRIC: Record<number, DevelopmentalArea | null> = {
  1: 'early language development',
  2: 'pediatric articulation/phonology',
  3: 'pediatric receptive-expressive language disorder',
  4: 'early language development',
  5: 'fluency',
};
const CONCERN_TO_AREA_MAP_ADULTS: Record<number, DevelopmentalArea | null> = {
  1: 'adult articulation',
  2: 'adult fluency',
  3: null,
  4: null,
  5: 'gender affirming voice',
  6: 'professional/public speaking',
  7: 'accent modification',
};

export const COMMUNICATION_CONCERNS_TPREFIX = {
  child: 'dropdowns.miniscreener.concerns.options.child',
  adult: 'dropdowns.miniscreener.concerns.options.adult',
};

const OPTION_OTHER: SelectOption = {
  label: 'other',
  value: 'other',
};

export const COMMUNICATION_CONCERNS_OPTIONS_ADULT: SelectOption[] = [
  ...Object.keys(CONCERN_TO_AREA_MAP_ADULTS).map(key => ({
    label: key,
    value: key,
  })),
  OPTION_OTHER,
];

export const COMMUNICATION_CONCERNS_OPTIONS_CHILD: SelectOption[] = [
  ...Object.keys(CONCERN_TO_AREA_MAP_PEDIATRIC).map(key => ({
    label: `${key}`,
    value: `${key}`,
  })),
  OPTION_OTHER,
];

const removeOtherOption = (concernIndexes: TherapyConcerns['concerns']) =>
  concernIndexes.filter(concern => concern !== OPTION_OTHER.value);

export const calculateDetectedAreas = (concerns: TherapyConcerns['concerns'], target: TherapyTarget) => {
  const data = target === 'child' ? CONCERN_TO_AREA_MAP_PEDIATRIC : CONCERN_TO_AREA_MAP_ADULTS;

  const concernIndexes = removeOtherOption(concerns);

  let detectedAreas: DevelopmentalArea[] = [];

  concernIndexes.forEach(value => {
    const index = parseInt(value);
    if (isNumber(index)) {
      const area = data[index];

      if (!isNull(area)) {
        detectedAreas.push(area);
      }
    }
  });

  detectedAreas = uniq(detectedAreas);

  return detectedAreas as DevelopmentalArea[];
};

/**
 * This function is used to calculate detected areas without filtering out the null values and "other concern" option
 * @param concerns
 * @param target
 * @returns detectedAreas as an array of DevelopmentalArea or null
 */
export const calculateDetectAreasRaw = (concerns: TherapyConcerns['concerns'], target: TherapyTarget) => {
  const data = target === 'child' ? CONCERN_TO_AREA_MAP_PEDIATRIC : CONCERN_TO_AREA_MAP_ADULTS;

  let detectedAreas: (DevelopmentalArea | null)[] = [];

  concerns.forEach(value => {
    const index = parseInt(value);
    if (isNumber(index)) {
      detectedAreas.push(data[index]);
    }
  });

  detectedAreas = uniq(detectedAreas);

  return detectedAreas;
};

export const mergeCommunicationConcerns = (
  { extraInformation, otherConcerns, concerns }: TherapyConcerns,
  target: TherapyTarget,
) => {
  const tPrefix = target === 'child' ? COMMUNICATION_CONCERNS_TPREFIX.child : COMMUNICATION_CONCERNS_TPREFIX.adult;

  const concernIndexes = removeOtherOption(concerns);

  let communicationConcerns = '';

  concernIndexes.forEach(index => (communicationConcerns += `Concern: ${i18next.t(`${tPrefix}.${index}`)} \n`));

  if (otherConcerns) {
    communicationConcerns += `Other communication concerns: ${otherConcerns} \n`;
  }

  if (extraInformation) {
    communicationConcerns += `Extra information: ${extraInformation}`;
  }

  return communicationConcerns;
};
